import {Injectable, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {MultiLanguageText} from '../../../models/dpp/multi-language-text';

@Injectable({
  providedIn: 'root',
})
export class LocalizationHelper {
  private static readonly ENGLISH_LANGUAGE_CODE = 'en';

  private readonly translate = inject(TranslateService);

  /**
   * Resolves a localized text value from either a plain string or a multilingual array.
   *
   * Fallback chain (first non-empty value wins):
   * 1. Direct string input (`value` when it is a non-empty string)
   * 2. Entry matching the current UI language (`TranslateService.currentLang`)
   * 3. Entry matching English (`en`)
   * 4. Entry matching `fallbackLanguage` (if provided)
   * 5. First array entry (`value[0]`)
   * 6. `fallbackText`
   *
   * Empty strings are treated as missing values during resolution.
   */
  getLocalizedText(
    value: MultiLanguageText[] | string | undefined,
    fallbackText = '—',
    fallbackLanguage?: string,
  ): string {
    if (typeof value === 'string' && value !== '') {
      return value;
    }

    if (!Array.isArray(value) || value.length <= 0) {
      return fallbackText;
    }

    const localizedText = this.findNonEmptyTextByLanguage(
      value,
      this.translate.currentLang,
    );
    if (localizedText) {
      return localizedText;
    }

    const englishText = this.findNonEmptyTextByLanguage(
      value,
      LocalizationHelper.ENGLISH_LANGUAGE_CODE,
    );
    if (englishText) {
      return englishText;
    }

    const fallbackLanguageText = this.findNonEmptyTextByLanguage(
      value,
      fallbackLanguage,
    );
    if (fallbackLanguageText) {
      return fallbackLanguageText;
    }

    const firstValueText = value.at(0)?.text;
    if (this.hasNonEmptyText(firstValueText)) {
      return firstValueText;
    }

    return fallbackText;
  }

  private findNonEmptyTextByLanguage(
    value: MultiLanguageText[],
    language: string | undefined,
  ): string | undefined {
    if (!language) {
      return undefined;
    }

    const localizedValue = value.find((item) => item.language === language);
    return this.hasNonEmptyText(localizedValue?.text)
      ? localizedValue?.text
      : undefined;
  }

  private hasNonEmptyText(text: string | undefined): text is string {
    return text !== undefined && text !== '';
  }
}
